import { useState } from "react";
import {
  Alert,
  Button,
  DialogTitle,
  FormControl,
  FormLabel,
  Modal,
  ModalClose,
  ModalDialog,
  Stack,
  Textarea,
  Typography,
} from "@mui/joy";
import {
  type Deal,
  dealError,
  proposalDealsApi,
} from "../../lib/proposalDealsApi";

export function ManualRepairAuthorization({
  deal,
  pending,
  refresh,
}: {
  deal: Deal;
  pending: boolean;
  refresh: () => Promise<void>;
}) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const hasScope =
    deal.repair_items.length > 0 &&
    deal.repair_items.every(
      (item) => item.location.trim() && item.solution.trim(),
    );
  const authorize = async () => {
    setBusy(true);
    setError("");
    try {
      await proposalDealsApi.action(deal.id, "mark-authorized", {
        revision: deal.revision,
        reason: reason.trim(),
      });
      await refresh();
      setOpen(false);
    } catch (e) {
      setError(dealError(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <>
      <Button
        variant="outlined"
        disabled={pending || !hasScope}
        onClick={() => setOpen(true)}
      >
        Mark as Authorized
      </Button>
      <Modal
        open={open}
        onClose={() => {
          if (!busy) setOpen(false);
        }}
      >
        <ModalDialog
          sx={{
            width: "min(520px, calc(100vw - 32px))",
            maxHeight: "90dvh",
            overflowY: "auto",
          }}
        >
          <ModalClose disabled={busy} />
          <DialogTitle>Mark repair as authorized?</DialogTitle>
          <Typography>
            Use this when the customer approved the work outside ApexOS,
            including repairs completed on site.
          </Typography>
          <Alert color="primary">
            This records your name and the current scope and price. It does not
            create a customer signature. Any outstanding signing request will be
            cancelled.
          </Alert>
          <Typography level="title-lg">
            Authorized total:{" "}
            {Number(deal.totals?.total || 0).toLocaleString("en-US", {
              style: "currency",
              currency: "USD",
            })}
          </Typography>
          <FormControl required>
            <FormLabel>Authorization note</FormLabel>
            <Textarea
              minRows={3}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              slotProps={{ textarea: { maxLength: 2000 } }}
              placeholder="For example: Customer approved the scope and price on site; repairs completed today."
            />
          </FormControl>
          <Typography level="body-sm">
            After saving, an Org Admin can mark the opportunity Won and create
            its job. Future scope or price changes require a linked revision.
          </Typography>
          {error && <Alert color="danger">{error}</Alert>}
          <Stack
            direction="row"
            gap={1}
            justifyContent="flex-end"
            flexWrap="wrap"
          >
            <Button
              variant="plain"
              disabled={busy}
              onClick={() => setOpen(false)}
            >
              Cancel
            </Button>
            <Button
              loading={busy}
              disabled={!reason.trim()}
              onClick={() => void authorize()}
            >
              Confirm authorization
            </Button>
          </Stack>
        </ModalDialog>
      </Modal>
    </>
  );
}
